import { useQuery } from '@tanstack/react-query';

import { clockTime, relativeTime, untilTime } from '../../../shared/lib/format';
import { Button } from '../../../shared/ui/button';
import { Note } from '../../../shared/ui/note';
import { InfoDot } from '../../../shared/ui/tooltip';
import { SectionPanel } from '../../../shared/ui/section-panel';
import { ErrorState, LoadingState } from '../../../shared/ui/states';
import {
  failureNote,
  listIpRanges,
  subnetCount,
  verdictOf,
  type TIpRangeStatus,
} from '../../../entities/ip-range';
import { useRefreshRanges, type IRefreshRanges } from '../use-refresh-ranges';
import { RefreshOutcomes } from './refresh-outcomes';

interface IStateProps {
  row: TIpRangeStatus;
  now: number;
  timeZone?: string;
  only?: boolean;
}

const Fresh = ({ row, now, timeZone, only }: IStateProps) => (
  <div className="px-3 py-3">
    <p className="text-data text-primary">
      {only
        ? `${subnetCount(row.cidrCount)} · the only thing still filtering`
        : `${subnetCount(row.cidrCount)} published by ${row.provider}`}
    </p>
    <p className="mt-1 text-meta text-muted">
      refreshed {relativeTime(row.fetchedAt, now, timeZone)}
    </p>
  </div>
);

const Stale = ({ row, now, timeZone }: IStateProps) => (
  <div className="px-3 py-3">
    <p className="text-data text-motion">
      serving the list from {clockTime(row.fetchedAt, timeZone)} · the refresh could not reach{' '}
      {row.provider}
    </p>
    <p className="mt-1 text-meta text-muted">
      {row.nextRefreshAt
        ? `next try ${untilTime(row.nextRefreshAt, now)}`
        : 'nothing is scheduled to try again'}
      {failureNote(row) && ` · ${failureNote(row)}`}
    </p>
    <p className="mt-3 flex items-center gap-inset text-meta text-muted">
      Filtering still runs, on addresses that may be out of date.
      <InfoDot>
        A refresh that cannot reach the provider answers with success on purpose — a list that
        narrows on a network error starts refusing live traffic.
      </InfoDot>
    </p>
  </div>
);

const Never = ({ row }: { row: TIpRangeStatus }) => (
  <div className="px-3 py-3">
    <Note tone="danger">
      <span className="text-data leading-20 text-primary">
        nothing has ever been downloaded — every request is rejected
      </span>
      <span className="mt-2 flex items-center gap-inset">
        Until {row.provider} answers once, there is nothing to match a sender against.
        <InfoDot>
          This is the only one of the three states that is an incident, and it opens an issue on its
          own.
        </InfoDot>
      </span>
    </Note>
  </div>
);

// Published is not filtering: without an ip_allowlist method pointing at the list, nothing reads it.
const Unused = ({
  row,
  onRequire,
  requiring,
}: {
  row: TIpRangeStatus;
  onRequire?: () => void;
  requiring?: boolean;
}) => (
  <div className="flex items-center gap-inset px-3 py-3">
    <span className="min-w-0 flex-1">
      <span className="block text-data text-primary">
        {verdictOf(row).health === 'never'
          ? `${row.provider} publishes its addresses — not used to filter this source`
          : `${row.provider} publishes ${subnetCount(row.cidrCount)} — not used to filter this source`}
      </span>
      <span className="mt-1 block text-meta text-muted">
        A request from anywhere else is accepted as long as its signature matches.
      </span>
    </span>
    {onRequire && (
      <Button
        variant="ghost"
        size="sm"
        className="h-control-xs shrink-0"
        waiting={requiring}
        waitingLabel="Adding"
        onClick={onRequire}
      >
        Also require these addresses
      </Button>
    )}
  </div>
);

const RangeBlock = ({
  row,
  refresh,
  now,
  timeZone,
  only,
  filtering,
  onRequire,
  requiring,
  className,
}: IStateProps & {
  refresh: IRefreshRanges;
  filtering: boolean;
  onRequire?: () => void;
  requiring?: boolean;
  className?: string;
}) => {
  const health = verdictOf(row).health;

  return (
    <SectionPanel
      className={className}
      title="Sender addresses"
      note={filtering ? 'filtering' : 'not filtering'}
      actions={
        <Button
          variant="ghost"
          size="sm"
          className="h-control-xs"
          waiting={refresh.busy === row.key}
          waitingLabel="Fetching"
          disabled={refresh.busy !== null}
          onClick={() => refresh.run(row.key)}
        >
          Refresh
        </Button>
      }
    >
      {!filtering && <Unused row={row} onRequire={onRequire} requiring={requiring} />}
      {filtering && health === 'fresh' && (
        <Fresh row={row} now={now} timeZone={timeZone} only={only} />
      )}
      {filtering && health === 'serving_stale' && <Stale row={row} now={now} timeZone={timeZone} />}
      {filtering && health === 'never' && <Never row={row} />}
    </SectionPanel>
  );
};

interface ISenderAddressesProps {
  keys: string[];
  filteringKeys: string[];
  now: number;
  timeZone?: string;
  only?: boolean;
  onRequire?: (key: string) => void;
  requiring?: boolean;
  requireError?: unknown;
  className?: string;
}

export const SenderAddresses = ({
  keys,
  filteringKeys,
  now,
  timeZone,
  only,
  onRequire,
  requiring,
  requireError,
  className,
}: ISenderAddressesProps) => {
  const refresh = useRefreshRanges();
  const ranges = useQuery({
    queryKey: ['ip-ranges'],
    queryFn: listIpRanges,
    enabled: keys.length > 0,
  });

  if (keys.length === 0) return null;

  const rows = (ranges.data ?? []).filter((row) => keys.includes(row.key));

  return (
    <div className={className}>
      {ranges.isError && <ErrorState error={ranges.error} onRetry={() => void ranges.refetch()} />}
      {ranges.isPending && !ranges.isError && <LoadingState rows={2} />}

      {rows.map((row) => (
        <RangeBlock
          key={row.key}
          row={row}
          refresh={refresh}
          now={now}
          timeZone={timeZone}
          only={only}
          filtering={filteringKeys.includes(row.key)}
          onRequire={onRequire && (() => onRequire(row.key))}
          requiring={requiring}
          className="mt-3 first:mt-0"
        />
      ))}

      {requireError !== null && requireError !== undefined && (
        <div className="mt-3">
          <ErrorState error={requireError} />
        </div>
      )}

      {!ranges.isPending &&
        !ranges.isError &&
        keys
          .filter((key) => !rows.some((row) => row.key === key))
          .map((key) => (
            <p key={key} className="mt-3 text-meta text-faint">
              <span className="font-mono">{key}</span> — no published list has been set up under
              this name, so nothing is downloaded for it.
            </p>
          ))}

      {refresh.error !== null && <ErrorState error={refresh.error} />}
      {refresh.outcomes && (
        <div className="mt-3">
          <RefreshOutcomes
            outcomes={refresh.outcomes.filter((outcome) => keys.includes(outcome.key))}
            onDismiss={refresh.dismiss}
          />
        </div>
      )}
    </div>
  );
};
